import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import AgGrid, { AgGridWrapper } from '../../common/AgGrid/index.jsx';

import { fetchJson } from '../../../helpers/index.js';
import LoadingIndicator from '../../common/loading_indicator.jsx';
import useContextMenu from '../hooks/useContextMenu.js';
import useRowId from '../hooks/useRowId.js';
import ColumnConfigurationPanel from './ColumnConfigurationPanel.jsx';
import { resolveSelectionProps } from '../hooks/useDynamicCols.jsx';
import { valueProps } from './ColumnConfigurationPanel.jsx';
import { useSelectionColumnClick } from '../hooks/useSelectionColumnClick.js';

import { datagrid_grouped_value_get_path } from '~/routes';

const permuteArrays = (first, next, ...rest) => {
  if (rest.length) next = permuteArrays(next, ...rest);
  return first.flatMap(a => next.map(b => [a, b].flat()));
}

// Build initial column defs based on column_defs received from parent.
// Only take the values with active grouping. Lock & pin left
const buildInitialValues = (id, colState, parentGridColDefs) => {
  if (id) { // The stored values will be loaded
    return {column_defs: {}}
  }
  const groupingEnabled = colState
    .filter(c => c.rowGroup)
    .sort((a,b) => a.rowGroupIndex - b.rowGroupIndex);

  const groupColumns = groupingEnabled  
    .map((c, idx) => ({
      ...parentGridColDefs.find(d => d.field == c.colId),
      rowGroup: idx != groupingEnabled.length - 1 && c.rowGroup,
      hide: idx != groupingEnabled.length - 1 && c.rowGroup,
      pinned: 'left',
      lockPinned: true
    }));

    return {column_defs: groupColumns};
}

// The functions can't be loaded from column def stored in the backend. 
// Need to add the value parser / formatter to load the selection options correctly
const buildColumnDefs = (columnDefs) => {
  return columnDefs.map(c => {
    if (c?.cellRendererParams?.original) {
      c = resolveSelectionProps(c.cellRendererParams.original, c)
    } else {
      c = {...c, ...valueProps}
    }
    return c
  })

}

const GroupedValueGrid = forwardRef(({datagrid, context, parentGridColDefs, colState, onClose, id}, ref) => {
  const [loading, setLoading] = useState(!!id);
  const [defaultValues, setDefaultValues] = useState(buildInitialValues(id, colState, parentGridColDefs));

  useEffect(()=> {
    if (!id) return;

    fetchJson(datagrid_grouped_value_get_path({id: id, context_name: context.context_name, _options: true})).then(res => {
      setDefaultValues(res);
      setLoading(false);
    })
  }, [id]);

  const gridRef = useRef(null);
  const nameRef = useRef(null);
  const getContextMenuItems = useContextMenu();

  const loadingOverlayComponent = useMemo(() => {
    return LoadingIndicator;
  }, []);

  const [_, getRowId] = useRowId();


  const gridReady = (event) => {
    gridRef.current.api.expandAll();
  }

  const defaultColDef = useMemo( ()=> ({
      sortable: false,
      editable: false,
      suppressHeaderMenuButton: true,
      pinned: 'left'
  }),[]);
  

  const columnDefs = useMemo(() => {
    if (loading) return [];
    if (defaultValues.column_defs) return buildColumnDefs(defaultValues.column_defs);

    return []
  }, [defaultValues, loading]);

  const getState = () => {  
    let state = {
      datagrid_id: datagrid.id,
      name: nameRef.current.value,
      column_defs: gridRef.current.api.getColumnDefs(),
      row_data: []
    }
    if(id) state.id = id;

    gridRef.current.api.forEachNode((rowNode, index) => {
      if (rowNode.data) state.row_data.push(rowNode.data);
    });

    return state;
  }

  useImperativeHandle(ref, () => {
    return {
      getState: () => {
        return getState();
      },
    };
  }, []);
  
  const rowData = useMemo(() => {
    // Todo: Merge the calculated options & stored values in case options get updated
    if (loading) return [];
    if (defaultValues.row_data) return defaultValues.row_data;

    const possibleValues = defaultValues.column_defs
      .filter(c => c.pinned == 'left')
      .sort((a,b) => a.rowGroupIndex - b.rowGroupIndex)
      .map(a => a.cellEditorParams.values.map(s => ({[a.field]: s})));

    if (possibleValues.length == 1) return possibleValues.flat();

    return permuteArrays(...possibleValues).map(a => a.reduce((a,b)=> ({...a, ...b}),{}))
  }, [columnDefs, loading])


  const getRowClass = params => {
    let className = '';
    if (params.node.group) {
        className += 'dg-row-group';
        className += ` dg-row-group-bg-${params.node.rowGroupIndex + 1}`
    }
    return className;
  };

  const sidePanel = useMemo(() => ({
    toolPanels: [
      {
        id: 'configure',
        labelDefault: 'Configure',
        labelKey: 'configure',
        iconKey: 'columns',
        toolPanel: ColumnConfigurationPanel,
      }
    ],
    defaultToolPanel: 'configure'
  }), []);

  if (loading) return <LoadingIndicator />

  return (
    <AgGridWrapper 
    wrapperStyle={{
      height: 'calc(100vh - 60px - 30px - 52px - 62px - 2rem)', // Viewport height - dialog margin - dialog header - dialog footer - bottom margin
    }}
  >
    <input type='text' ref={nameRef} defaultValue={defaultValues.name} className='form-control mb-1' placeholder={I18n.t('name')} />

    <AgGrid
      rowData={rowData}
      ref={gridRef} // Ref for accessing Grid's API
      columnDefs={columnDefs}
      defaultColDef={defaultColDef}
      style={{flex: 1}}
      animateRows={true}
      sideBar={sidePanel}
      getRowId={getRowId}
      cellSelection={true}
      loadingOverlayComponent={loadingOverlayComponent}
      groupHeaderHeight={20}
      onGridReady={gridReady}
      stopEditingWhenCellsLoseFocus
      getContextMenuItems={getContextMenuItems}
      groupDisplayType='multipleColumns'
      groupLockGroupColumns={-1}
      getRowClass={getRowClass}
      suppressAggFuncInHeader={true}
      rowHeight={25}
      grandTotalRow={'bottom'}
      onCellClicked={useSelectionColumnClick()}
    />

    </AgGridWrapper>
  )
});

export default GroupedValueGrid;